/**
 * Shapes of the /admin endpoints. The admin API has no zod contracts of its own, so these mirror
 * apps/api/src/modules/admin/admin.service.ts (lean Mongo docs serialised as JSON: dates are ISO
 * strings). Everything that *does* exist in @lc/contracts is imported from there, type-only, so the
 * bundle never pulls in zod.
 */
import type {
  AdminRole,
  Appearance,
  CHECK_TIERS,
  CheckTier,
  EventKind,
  Gender,
  JOB_STATES,
  JobState,
  MangaPhase,
  NpcId,
  ReportReason,
  ReportTarget,
} from '@lc/contracts';

export type IsoDate = string;

// ---------- vocabularies (values checked against the contract types) ----------

export const JOB_STATE_LIST = ['queued', 'running', 'retry_scheduled', 'succeeded', 'failed_permanent', 'cancelled'] as const satisfies typeof JOB_STATES;
export const TIERS = ['strong', 'normal', 'awkward', 'blocked'] as const satisfies typeof CHECK_TIERS;

/** JobDoc['type'] on the server. */
export const JOB_TYPES = ['story.generate', 'comic.render', 'shift.pack', 'npc.reply', 'data.export', 'data.delete', 'prereg.launch'] as const;
export type JobType = (typeof JOB_TYPES)[number];

export const FUNNEL_STEPS = ['onboarding_completed', 'first_story_started', 'story_completed', 'comic_ready', 'comic_opened', 'purchase_verified'] as const;
export type FunnelStep = (typeof FUNNEL_STEPS)[number];

export const REPORT_STATES = ['open', 'actioned', 'dismissed'] as const;
export type ReportState = (typeof REPORT_STATES)[number];

export const DECISION_ACTIONS = ['dismiss', 'warn', 'remove_message', 'suspend_user'] as const;
export type DecisionAction = (typeof DECISION_ACTIONS)[number];

export type UserStatus = 'active' | 'suspended' | 'deleting' | 'deleted';

/** Flag key the image service polls: enabled = new illustrated art generation is stopped. */
export const IMAGES_KILL_SWITCH = 'images_disabled';

export type { AdminRole, CheckTier, Gender, JobState };

// ---------- GET /admin/metrics ----------

export interface Metrics {
  users: { total: number; dau: number; wau: number };
  today: { storiesCompleted: number; chaptersReady: number };
  safety: { openReports: number };
  jobs: { queue: Array<{ type: JobType; state: JobState; count: number }>; failedLast24h: number };
  ai: {
    spentTodayUsd: number;
    byPurpose: Array<{ purpose: string; usd: number; calls: number; avgLatencyMs: number }>;
    /** Only days with spend are present; `day` is a UTC YYYY-MM-DD. */
    last7Days: Array<{ day: string; usd: number }>;
  };
  /** Purchases grouped by product id; `credits` is the credits granted by those purchases. */
  revenue7d: Array<{ product: string; purchases: number; credits: number }>;
  /** Distinct users per event over 7 days; steps with no events are absent. */
  funnel7d: Partial<Record<FunnelStep, number>>;
}

// ---------- reports ----------

export interface Report {
  _id: string;
  reporterUserId: string;
  targetType: ReportTarget;
  targetId: string;
  targetUserId: string | null;
  reason: ReportReason;
  note: string | null;
  evidence: unknown;
  autoFlags: string[];
  state: ReportState;
  decision: { adminId: string; action: DecisionAction; note: string; at: IsoDate } | null;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}

export interface DecisionBody {
  action: DecisionAction;
  note: string;
}

// ---------- users ----------

export interface Wallet {
  _id: string;
  credits: number;
  reserved: number;
  revision: number;
  updatedAt: IsoDate;
}

export interface CreditsBody {
  /** Non-zero integer in [-1000, 1000]. */
  amount: number;
  reason: string;
}

// ---------- jobs ----------

export interface Job {
  _id: string;
  type: JobType;
  state: JobState;
  attempt: number;
  error: string | null;
  payload: Record<string, unknown>;
  userId: string | null;
  phase: MangaPhase | null;
  updatedAt: IsoDate;
}

// ---------- content ----------

export interface TemplateStats {
  key: string;
  title: string;
  kind: EventKind;
  focusNpc: NpcId | null;
  beats: number;
  started: number;
  completed: number;
  abandoned: number;
  outcomes: Record<string, number>;
}

export interface ContentReport {
  release: string;
  issues: Array<{ path: string; message: string }>;
  templates: TemplateStats[];
}

export interface TemplateSummary {
  key: string;
  kind: EventKind;
  title: string;
}

export interface SimulateBody {
  gender: Gender;
  statLevel: number;
  met: boolean;
}

/** TemplateLock from @lc/domain; `condition` is an authored Condition tree. */
export type TemplateLock = { kind: 'condition'; condition: unknown } | { kind: 'completed' } | { kind: 'slot' };

export interface SimulationResult {
  templateKey: string;
  availableForBuild: boolean;
  lock: TemplateLock | null;
  paths: number;
  outcomes: Record<string, number>;
  unreachable: string[];
  tierMix: Record<CheckTier, number>;
  sample: Array<{ path: string[]; outcome: string; tiers: CheckTier[] }>;
}

/** The server stops walking after this many paths. */
export const SIMULATION_PATH_CAP = 400;

// ---------- flags & audit ----------

export interface FeatureFlag {
  _id: string;
  enabled: boolean;
  rolloutPercent?: number;
  note: string;
  createdAt?: IsoDate;
  updatedAt?: IsoDate;
}

export interface FlagBody {
  enabled: boolean;
  note: string;
}

export interface AuditEntry {
  _id: string;
  actorId: string;
  action: string;
  targetType: string;
  targetId: string;
  reason: string;
  before: unknown;
  after: unknown;
  createdAt: IsoDate;
}

export interface Ok {
  ok: true;
}

// ---------- GET /admin/retention ----------

/**
 * One signup cohort (UTC day). `d1`/`d7` are shares in [0, 1] of that cohort whose last activity is
 * at least 1 / 7 days after signup. Only days with signups are present (last 14 days).
 */
export interface RetentionRow {
  day: string;
  joined: number;
  d1: number;
  d7: number;
}

// ---------- operator console (/admin/me, dashboard, players, story, manga, notify, team) ----------

export type RangeDays = 1 | 7 | 30;

export interface Me {
  userId: string;
  email: string | null;
  roles: AdminRole[];
  sessionHours: number;
}

export interface TeamMember {
  userId: string;
  email: string | null;
  roles: AdminRole[];
  lastActiveAt: IsoDate;
  status: UserStatus;
}

export interface RevenueSource {
  purchases: number;
  /** Amount actually paid, per currency (major units). Store events without a price add nothing here. */
  gross: Record<string, number>;
  /** Catalogue HK$ price of what was bought: a like-for-like figure across sources. */
  nominalHkd: number;
  refunds: number;
  refunded: Record<string, number>;
}

/** Real money sources, then the test ones (Stripe test mode, store sandbox), which are never revenue. */
export const REVENUE_SOURCES = ['app_store', 'stripe', 'app_store_sandbox', 'stripe_test'] as const;
export type RevenueSourceKey = (typeof REVENUE_SOURCES)[number];

export interface Dashboard {
  range: RangeDays;
  since: IsoDate;
  players: {
    active: number;
    signups: number;
    guestSignups: number;
    activeByDay: Array<{ day: string; users: number }>;
    signupsByDay: Array<{ day: string; accounts: number; guests: number }>;
  };
  stories: { played: number; playedByDay: Array<{ day: string; n: number }>; aiWritten: number; aiFailed: number };
  manga: { basic: number; illustrated: number; illustratedPartial: number; failedJobs: number; costUsd: number };
  ai: { budgetUsd: number; todayUsd: number; totalUsd: number; byDay: Array<{ day: string; textUsd: number; imageUsd: number; calls: number }> };
  revenue: { bySource: Partial<Record<RevenueSourceKey, RevenueSource>>; byProduct: Array<{ product: string; purchases: number }> };
  credits: { issued: number; spent: number; refunded: number; adjustedDown: number };
  /** The same headline numbers for the window just before this one. */
  previous: {
    active: number;
    signups: number;
    guestSignups: number;
    played: number;
    aiWritten: number;
    aiFailed: number;
    illustrated: number;
    basic: number;
    aiUsd: number;
    revenueHkd: number;
    creditsIssued: number;
    creditsSpent: number;
  };
  /** What needs someone now, whatever the range. */
  attention: { failedJobs: number; openReports: number; failedChapters: number; issueChapters: number; budgetShare: number };
}

/** Days the AI cost page can look back over. */
export type AiCostDays = 7 | 30 | 90;

/** GET /admin/growth: the website's pre-registrations and shared chapters (counts only). */
export interface Growth {
  prereg: {
    total: number;
    notified: number;
    giftsClaimed: number;
    giftsUsed: number;
    byRef: Counted[];
    byLocale: Counted[];
    daily: Array<{ day: string; count: number }>;
    launching: boolean;
  };
  shares: {
    active: number;
    views: number;
    finished: number;
    cta: number;
    preregs: number;
    refs: Counted[];
    top: GrowthShare[];
  };
}
export interface Counted {
  key: string;
  count: number;
}
export interface GrowthShare {
  shareId: string;
  url: string | null;
  title: string | null;
  userId: string;
  sharedBy: string | null;
  createdAt: string;
  views: number;
  finished: number;
  cta: number;
  refs: Counted[];
}

export type MemberTier = 'pass' | 'plus' | 'premium';
export type InsightDays = 7 | 30 | 90;
export const INSIGHT_FUNNEL = ['app_opened', 'onboarding_completed', 'first_story_started', 'story_completed', 'paywall_viewed', 'purchase_started', 'purchase_verified'] as const;
export type InsightStep = (typeof INSIGHT_FUNNEL)[number];

/** GET /admin/insights: the funnel, memberships now, NPC following and the coin economy. */
export interface Insights {
  range: InsightDays;
  since: IsoDate;
  funnel: Array<{ name: InsightStep; users: number }>;
  /** Running memberships by tier: bought in a store or on the website, or granted by support/promo. */
  members: Partial<Record<MemberTier, { store: number; granted: number }>>;
  saves: number;
  /** Met NPCs across every save, by relationship stage. */
  stages: Record<string, number>;
  npcs: Array<{ npcId: string; name: string; portraitUrl: string; met: number; stages: Record<string, number>; avgAffection: number }>;
  coins: { total: number; median: number; p90: number; max: number; earned: number; spent: number; byType: Array<{ type: string; direction: 'in' | 'out'; coins: number; events: number }> };
}

/** GET /admin/ai-cost: AI spend per player against the planning target (a yardstick, never a limit). */
export interface AiCost {
  range: AiCostDays;
  /** First and last UTC day of the window (today included). */
  from: string;
  to: string;
  /** About US$2 per player per 30 days; `usd` is that pro-rated to this window. */
  target: { usdPer30Days: number; usd: number };
  /** Everything in the window, system calls (no player) included. */
  totalUsd: number;
  systemUsd: number;
  calls: number;
  /** Players with any AI usage in the window; system calls left out. */
  players: { count: number; totalUsd: number; averageUsd: number; medianUsd: number; overTarget: number; overTargetShare: number };
  byPurpose: Array<{ purpose: string; usd: number; systemUsd: number; calls: number; images: number }>;
  /** The 50 most expensive players. */
  top: AiCostPlayer[];
}

export interface AiCostPlayer {
  userId: string;
  email: string | null;
  publicId: string | null;
  displayName: string | null;
  characterName: string | null;
  isGuest: boolean | null;
  status: UserStatus | null;
  /** A real (non-sandbox) purchase, or bought Plus that is still on. */
  paying: boolean;
  totalUsd: number;
  calls: number;
  /** Images drawn (manga pages, portraits). */
  images: number;
  firstDay: string;
  lastDay: string;
  byPurpose: Array<{ purpose: string; usd: number; calls: number }>;
}

export interface StorySummary {
  failed: number;
  issues: number;
  ready: number;
  generating: number;
}

export interface PlayerRow {
  _id: string;
  email: string | null;
  publicId: string;
  status: UserStatus;
  isGuest: boolean;
  displayName: string | null;
  createdAt: IsoDate;
  lastActiveAt: IsoDate;
  adminRoles: AdminRole[];
  characterName: string | null;
}

export interface LedgerRow {
  _id: string;
  kind: 'grant' | 'purchase' | 'reserve' | 'settle' | 'release' | 'refund' | 'adjust' | 'reward';
  amount: number;
  balanceAfter?: number;
  operationKey: string;
  globalKey?: string | null;
  reason?: string | null;
  ref?: string | null;
  adminId?: string | null;
  environment?: string;
  createdAt: IsoDate;
}

export interface EntitlementRow {
  _id: string;
  key: string;
  source: 'revenuecat' | 'stripe' | 'admin' | 'promo';
  status: 'active' | 'expired' | 'revoked';
  productId?: string | null;
  expiresAt: IsoDate | null;
  environment?: string;
  createdAt: IsoDate;
}

export interface PersonalChapterRow {
  _id: string;
  userId: string;
  scopeId: string;
  seq: number;
  status: 'planned' | 'generating' | 'ready' | 'played' | 'failed';
  lane: string;
  hook: { npcId: string | null; location: string; reason: string; careerId?: string; via?: string; threadKind?: string; spot?: string; lastBeat?: string };
  locale?: string;
  reviewIssues?: string[];
  opensThread?: { npcIds: string[]; kind: string; text: string; payoffWhen: string | null } | null;
  paysOffThread?: string | null;
  emotionalBeat?: string;
  endHook?: string;
  heartSkip?: string | null;
  spots?: string[];
  error: string | null;
  title?: string | null;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}

export interface ProseLine {
  speaker: string | null;
  text: string;
}

export interface StoryChapterDetail extends PersonalChapterRow {
  prose: {
    title: string;
    synopsis: string;
    beats: Array<{ id: string; background: string; lines: ProseLine[]; choices: Array<{ id: string; label: string; check: string | null; normal: ProseLine[] }> }>;
    outcomes: Array<{ id: string; title: string; summary: string; closing: ProseLine[] }>;
  } | null;
}

export interface StoryThread {
  _id: string;
  scopeId: string;
  userId: string | null;
  npcIds: string[];
  kind: string;
  text: string;
  openedBy: string;
  payoffWhen: string | null;
  status: 'open' | 'paid' | 'expired';
  createdDay: number;
  updatedAt: IsoDate;
}

export interface IllustrationSummary {
  pagesTotal: number;
  pagesDrawn: number;
  composedPages: number[];
  previewPages: number | null;
  creditsCharged: number;
}

export interface RenderRow {
  _id: string;
  chapterId: string;
  renderVersion: number;
  locale: string;
  tier: 'basic' | 'illustrated';
  qualityStatus: 'approved' | 'needs_review' | 'rejected';
  costMicrousd: number;
  illustration?: IllustrationSummary | null;
  repairOf?: string | null;
  title: string | null;
  ownerUserIds: string[];
  current: boolean;
  createdAt: IsoDate;
}

export interface RenderDetail extends Omit<RenderRow, 'title' | 'ownerUserIds' | 'current'> {
  panels: Array<{ panelId: string; index: number; page: number | null; lettering: 'overlay' | 'in_art'; status: 'ready' | 'fallback' | 'failed'; url: string }>;
  storyboard: unknown;
  chapter: { _id: string; title: string; ownerUserIds: string[]; currentRenderId: string | null } | null;
}

export interface FailedRenderJob {
  _id: string;
  payload: Record<string, unknown>;
  error: string | null;
  userId: string | null;
  updatedAt: IsoDate;
}

export interface PlayerDetail {
  user: PlayerRow & {
    bio: string | null;
    ageGate: { passedAt: IsoDate; termsVersion: string } | null;
    preferences: { locale: string; city?: string | null; npcNotifications?: boolean };
    aiChapters?: { day: string; count: number } | null;
    energy?: { value: number; at: number } | null;
    freeIllustratedAt?: IsoDate | null;
    stripeCustomerId?: string | null;
    paymentHoldAt?: IsoDate | null;
    starterGift?: 'granted' | 'deferred' | 'denied' | null;
  };
  character: { _id: string; name: string; gender: Gender; trait?: string; lifeGoal?: string; appearance: Appearance } | null;
  avatarUrl: string | null;
  save: {
    day: number | null;
    coins: number;
    stats: Record<string, number>;
    relationships: Array<{ npcId: string; name: string; portraitUrl: string; stage: string; status: string; dims: Record<string, number>; metDay: number; statusDay: number | null }>;
  } | null;
  wallet: Wallet | null;
  /** Running memberships: the tier the player has now, their subscription (Plus/Premium) and 月卡. */
  membership: {
    tier: MemberTier | 'free';
    sub: { source: string; expiresAt: IsoDate | null; productId: string | null; tier: 'plus' | 'premium' | null } | null;
    pass: { source: string; expiresAt: IsoDate | null; productId: string | null } | null;
  };
  entitlements: EntitlementRow[];
  ledger: LedgerRow[];
  personalChapters: PersonalChapterRow[];
  chapters: Array<{ _id: string; title: string; phase: string; currentRenderId: string | null; createdAt: IsoDate; isCanonical: boolean }>;
  renders: Array<Pick<RenderRow, '_id' | 'chapterId' | 'tier' | 'renderVersion' | 'costMicrousd' | 'illustration' | 'createdAt'>>;
  reports: { filed: Report[]; against: Report[] };
  devices: Array<{ _id: string; platform: string | null; enabled: boolean; updatedAt: IsoDate }>;
  dataRequests: Array<{ _id: string; kind: string; status: string; createdAt: IsoDate }>;
  activeSessions: number;
}

export interface JobDetail extends Job {
  maxAttempts?: number;
  priority?: number;
  createdAt: IsoDate;
  [k: string]: unknown;
}

export interface ReportUser {
  _id: string;
  email?: string | null;
  publicId?: string;
  status?: UserStatus;
  displayName?: string | null;
}

export interface ReportWithContext extends Report {
  reporter: ReportUser | null;
  target: ReportUser | null;
  targetReportCount: number;
  message: { _id: string; body: string; senderUserId: string | null; moderation: string; createdAt: IsoDate } | null;
}

export const SEGMENTS = ['active7', 'all', 'plus', 'zh', 'en'] as const;
export type Segment = (typeof SEGMENTS)[number];

export interface BroadcastPreview {
  players: number;
  reachable: number;
  lastBroadcastAt: IsoDate | null;
  nextAllowedAt: IsoDate | null;
}

export interface PushBody {
  title: string;
  body: string;
  path: string;
  reason: string;
}

export interface AuditRow extends AuditEntry {
  actorEmail: string | null;
}

export interface AuditFilter {
  actor?: string | undefined;
  action?: string | undefined;
  target?: string | undefined;
  q?: string | undefined;
}

export interface JobSearch {
  state?: string | undefined;
  type?: string | undefined;
  userId?: string | undefined;
}
